import React from 'react';
import type moment from 'moment';
import { PickerDay } from '@mui/x-date-pickers/PickerDay';
import type { PickerDayProps } from '@mui/x-date-pickers/PickerDay';
import { avatarGenderClass, clientNameOf } from '../../../../views/Advisor/ClientV2/clientPresentation';
import type { QueueClient } from '../../../../types/client';
import './ReminderLoad.scss';

/**
 * How busy a day already is, shown where the adviser picks a reminder date.
 *
 * Two pieces, both fed by reminderLoadByDay: dots under each day in the picker,
 * and one line under the field naming who else is on the chosen day. The dots
 * follow the ClientV2 calendar's avatars — same gender colours, same cap of
 * three with the third slot turning into +N — shrunk to fit the picker's 36px
 * day button, which has no room for an avatar. Names only fit in the line.
 */

const DAY_KEY = 'YYYY-MM-DD';

/** Mirrors AVATAR_CAP in ClientV2/partials/ReminderCalendar.tsx. */
const DOT_CAP = 3;

/** How many names the line spells out before it counts the rest. */
const NAME_CAP = 3;

export type ReminderLoad = Map<string, QueueClient[]>;

export interface ReminderLoadDayProps extends PickerDayProps {
  load?: ReminderLoad;
  adviserEmail?: string;
}

/** The picker's `slots.day`; `load` and `adviserEmail` arrive via `slotProps.day`. */
export const ReminderLoadDay = (props: ReminderLoadDayProps) => {
  const { load, adviserEmail, ...dayProps } = props;
  const day = dayProps.day as moment.Moment;
  const clients = dayProps.outsideCurrentMonth || !load ? undefined : load.get(day.format(DAY_KEY));
  if (!clients || clients.length === 0) return <PickerDay {...dayProps} />;

  const shown = clients.slice(0, clients.length > DOT_CAP ? DOT_CAP - 1 : DOT_CAP);
  const hidden = clients.length - shown.length;

  return (
    <PickerDay {...dayProps} className={`${dayProps.className || ''} reminder-load-day`}>
      {day.format('D')}
      <span className="reminder-load-dots" data-testid="reminder-load-dots" aria-hidden="true">
        {shown.map(client => (
          <span key={client._id} className={`reminder-load-dot is-${avatarGenderClass(client, adviserEmail)}`} />
        ))}
        {hidden > 0 && <span className="reminder-load-more">+{hidden}</span>}
      </span>
    </PickerDay>
  );
};

interface ReminderLoadHintProps {
  /** Who else is on the chosen day; nothing renders when empty. */
  clients?: QueueClient[];
  locale?: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

export const ReminderLoadHint = ({ clients, locale, formatMessage }: ReminderLoadHintProps) => {
  if (!clients || clients.length === 0) return null;
  const separator = locale && locale.startsWith('zh') ? '、' : ', ';
  const names = clients.slice(0, NAME_CAP).map(clientNameOf).join(separator);
  const extra = clients.length - NAME_CAP;
  const listed = extra > 0 ? formatMessage({ id: 'reminder-load-more-names' }, { names, extra }) : names;
  return (
    <span className="reminder-load-hint" data-testid="reminder-load-hint" role="status">
      {formatMessage({ id: 'reminder-load-hint' }, { count: clients.length, names: listed })}
    </span>
  );
};
